import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

/**
 *
 * UserTabs
 *
 * WARP-939 — switches the Users page between dealer users and WPC users.
 *
 * A segmented control rather than a dropdown: there are exactly two options, both are
 * worth reading at a glance, and switching is the main thing anyone does on this page.
 *
 * Styled to match the Outstanding List / RO Intake / Billing group in `DealerHeader`,
 * which is the same control doing the same job one level down. Keep the two in step.
 */
const UserTabs = ({ activeTab, onChange, panelId, tabs }) => {
  const tabRefs = useRef({})

  // Arrow keys move between tabs, which is what a tablist is expected to do. Without
  // it, a keyboard user has to tab out of the control and back in to reach the other
  // side.
  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

    event.preventDefault()
    const index = tabs.findIndex((tab) => tab.id === activeTab)
    const offset = event.key === 'ArrowRight' ? 1 : -1
    const next = tabs[(index + offset + tabs.length) % tabs.length]

    onChange(next.id)
    tabRefs.current[next.id]?.focus()
  }

  return (
    <div
      aria-label="User type"
      className="bg-charcoal-100 inline-flex rounded-lg p-1"
      role="tablist"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab

        return (
          <button
            aria-controls={panelId}
            aria-selected={isActive}
            className={mergeClassNames(
              'rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-all',
              // focus-visible, not focus: the reference group has no ring at all, and a
              // halo left behind by a mouse click would be the one way these two differ.
              // Keyboard users still get it, which is who it is for.
              'focus-visible:ring-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
              isActive
                ? 'bg-white text-blue-800 shadow-sm'
                : 'text-charcoal-400 hover:text-charcoal-900',
            )}
            key={tab.id}
            onClick={() => onChange(tab.id)}
            onKeyDown={handleKeyDown}
            ref={(element) => {
              tabRefs.current[tab.id] = element
            }}
            role="tab"
            // Only the selected tab is in the tab order; the arrow keys reach the rest.
            tabIndex={isActive ? 0 : -1}
            type="button"
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

UserTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  panelId: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
}

export default UserTabs
